// client/src/pages/profile.jsx

import React, { useState, useContext, useEffect } from 'react';
import TwoFactorModal from '../../modals/auth/twoFactorModal';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import CustomAlert from '../../components/customs/customAlert';
import showNotification from '../../utilities/showNotification';
import { useNavigate } from 'react-router-dom';
import { GlobalContext } from '../../context/provider';
import authAction from "../../context/actions/authAction";
import useStudent from '../../hooks/useStudent';
import useInstructor from '../../hooks/useInstructor';
import useAuth from '../../hooks/useAuth';
import useAuditLog from '../../hooks/useAuditLog';
import useTokenUsage from '../../hooks/useTokenUsage';


import '../../styles/globalstyles.css';
import './profile.css';

const Profile = () => {
  const navigate = useNavigate();
  const { 
    authState: { authData },
    authDispatch,
  } = useContext(GlobalContext);

  const { getStudentByLoginId, updateStudent } = useStudent();
  const { getInstructorByLoginId, updateInstructor } = useInstructor();
  const { check2FAStatus, disable2FA, logout } = useAuth();
  const { exportActivities, getUserActivity, getActionsByRole } = useAuditLog();

  const { getSummary: getTokenSummary } = useTokenUsage();
  const [tokenSummary, setTokenSummary] = useState(null);

  const [profileData, setProfileData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertConfig, setAlertConfig] = useState({});

  const [backupCodesRemaining, setBackupCodesRemaining] = useState(0);
  const [show2FAModal, setShow2FAModal] = useState(false);

  const [isExporting, setIsExporting] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [filterAction, setFilterAction] = useState('all');
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [roleActions, setRoleActions] = useState([]);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const isStudent = authData?.role === 'student';
  const isInstructor = authData?.role === 'instructor';
  const isAdmin = authData?.role === 'admin';

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        let response;
        
        if (isStudent) {
          response = await getStudentByLoginId(authData?.id);
        } else if (isInstructor) {
          response = await getInstructorByLoginId(authData?.id);
        } else if (isAdmin) {
          setProfileData(authData);
          setFormData(authData);
          setIsLoading(false);
          return;
        }

        if (response?.success) {
          setProfileData(response.data);
          setFormData(response.data);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        showNotification('Failed to load profile', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    if (authData?.id) {
      fetchProfile();
    }
  }, [authData?.id, authData?.role]);

  useEffect(() => {
    const loadTokenSummary = async () => {
      const response = await getTokenSummary(authData?.role);
      if (response.success) {
        setTokenSummary(response.data);
      }
    };

    if (authData?.id) {
      loadTokenSummary();
    }
  }, [authData?.id, authData?.role]);

  useEffect(() => {
    const load2FAStatus = async () => {
      const status = await check2FAStatus();
      if (status.success) {
        setIs2FAEnabled(status.enabled);
        setBackupCodesRemaining(status.backupCodesRemaining || 0);
      }
    };

    if (authData?.id && !isAdmin) {
      load2FAStatus();
    }
  }, [authData?.id, isAdmin]);

  const validatePhone = (phone) => {
    const phoneRegex = /^[\d\s\-\(\)]+$/;
    if (!phone) return 'Phone number is required';
    if (phone.length < 10) return 'Phone number must be at least 10 digits';
    if (!phoneRegex.test(phone)) return 'Invalid phone number format';
    return null;
  };

  const validateDateOfBirth = (dob) => {
    if (!dob) return 'Date of birth is required';
    
    const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dob.match(dateRegex);
    
    if (!match) return 'Format must be DD/MM/YYYY';
    
    const day = parseInt(match[1]);
    const month = parseInt(match[2]);
    const year = parseInt(match[3]);
    
    if (month < 1 || month > 12) return 'Invalid month';
    if (day < 1 || day > 31) return 'Invalid day';
    
    const currentYear = new Date().getFullYear();
    const age = currentYear - year;
    if (age < 13) return 'Must be at least 13 years old';
    if (age > 120) return 'Invalid year';
    
    const date = new Date(year, month - 1, day);
    if (date.getDate() !== day) return 'Invalid date';
    
    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const cleaned = value.replace(/\D/g, '');
      let formatted = cleaned;
      
      if (cleaned.length >= 6) {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
      } else if (cleaned.length >= 3) {
        formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
      }
      
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else {
      console.log('Changing:', name, 'from', formData[name], 'to', value);
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleSave = async () => {
    const newErrors = {};
    
    const phoneError = validatePhone(formData.phone);
    if (phoneError) newErrors.phone = phoneError;
    
    if (isInstructor) {
      if (!formData.title?.trim()) {
        newErrors.title = 'Title is required';
      }
      if (!formData.department?.trim()) {
        newErrors.department = 'Department is required';
      }
      if (!formData.bio?.trim()) {
        newErrors.bio = 'Bio is required';
      } else if (formData.bio.length < 50) {
        newErrors.bio = 'Bio must be at least 50 characters';
      } else if (formData.bio.length > 1000) {
        newErrors.bio = 'Bio must not exceed 1000 characters';
      }
      if (!formData.expertise || (Array.isArray(formData.expertise) && formData.expertise.length === 0)) {
        newErrors.expertise = 'At least one area of expertise is required';
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSaving(true);
    try {
      let response;
      const updatePayload = { ...formData };
      
      if (isInstructor) {
        // Handle expertise conversion if it's a string
        if (typeof updatePayload.expertise === 'string') {
          updatePayload.expertise = updatePayload.expertise.split(',').map(e => e.trim()).filter(e => e);
        }
        if (typeof updatePayload.qualifications === 'string') {
          updatePayload.qualifications = updatePayload.qualifications.split(',').map(q => q.trim()).filter(q => q);
        }
      }
      
      if (isStudent) {
        response = await updateStudent(authData?.id, updatePayload);
      } else if (isInstructor) {
        response = await updateInstructor(authData?.id, updatePayload);
      }

      if (response?.success) {
        console.log('Response data:', response.data); // ADD THIS
  console.log('Gender in response:', response.data.gender); // ADD THIS
  
        setProfileData(response.data);
        setFormData(response.data);
        setIsEditing(false);
        showNotification('Profile updated successfully!', 'success');
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      console.error('Update failed:', error);
      showNotification('Failed to update profile', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(profileData);
    setIsEditing(false);
    setErrors({});
  };

  const handleLogout = async () => {
    await logout();
  };

  const handleExportActivities = async () => {
    setIsExporting(true);
    try {
      const response = await exportActivities(authData.id);
      if (response.success) {
        const dataStr = JSON.stringify(response.data, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `profix-activities-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showNotification('Activities exported successfully!', 'success');
      } else {
        showNotification('Failed to export activities.', 'error');
      }
    } catch (error) {
      showNotification('Failed to export activities.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleLoadActivityLogs = async () => {
    setActivityLoading(true);
    try {
      const [activityResponse] = await Promise.all([
        getUserActivity(authData.id, { limit: 100 }),
        loadRoleActions()
      ]);
      if (activityResponse.success) {
        setActivityLogs(activityResponse.data);
      }
    } catch (error) {
      console.error('Failed to load activity logs:', error);
    } finally {
      setActivityLoading(false);
    }
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    const intervals = {
      year: 31536000, month: 2592000, week: 604800,
      day: 86400, hour: 3600, minute: 60
    };
    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / secondsInUnit);
      if (interval >= 1) return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
    }
    return 'just now';
  };

  const handleDownloadActivityPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      showNotification('Generating PDF...', 'info');
      const { jsPDF } = await import('jspdf');

      const getBase64FromUrl = async (url) => {
        try {
          const res = await fetch(url);
          const blob = await res.blob();
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        } catch { return null; }
      };

      const logoBase64 = await getBase64FromUrl('/images/prof_ix.png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth  = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin     = 15;
      const maxWidth   = pageWidth - margin * 2;
      let y = margin;

      const checkPage = (needed = 10) => {
        if (y + needed > pageHeight - margin) {
          pdf.addPage();
          y = margin;
        }
      };

      const sanitize = (text) => String(text || '')
        .replace(/[^\x20-\x7E\n]/g, '')
        .replace(/[ \t]+/g, ' ')
        .trim();

      const addText = (text, fontSize = 10, color = [30, 30, 30], bold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setTextColor(...color);
        pdf.setFont('helvetica', bold ? 'bold' : 'normal');
        const lines = pdf.splitTextToSize(sanitize(text), maxWidth);
        checkPage(lines.length * (fontSize * 0.4 + 2));
        pdf.text(lines, margin, y);
        y += lines.length * (fontSize * 0.4 + 2);
      };

      const addDivider = () => {
        checkPage(6);
        pdf.setDrawColor(200, 200, 200);
        pdf.line(margin, y, pageWidth - margin, y);
        y += 4;
      };

      // Header with logo
      if (logoBase64) {
        pdf.addImage(logoBase64, 'PNG', margin, y, 28, 28);
        pdf.setFontSize(22);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 102, 204);
        pdf.text('Prof iX', margin + 32, y + 12);
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        pdf.text('Intelligent Learning Platform', margin + 32, y + 20);
        y += 34;
      } else {
        addText('Prof iX — Intelligent Learning Platform', 14, [0, 102, 204], true);
        y += 4;
      }
      addDivider();

      // Title
      addText('My Activity Log', 18, [20, 20, 20], true);
      y += 2;
      addText(`${profileData?.name || authData?.name} · ${authData?.role?.toUpperCase()} · Exported: ${new Date().toLocaleDateString()}`, 10, [100, 100, 100]);
      y += 4;
      addDivider();

      // Summary
      addText(`Total Records: ${filteredLogs.length}${filterAction !== 'all' ? ` · Filtered by: ${filterAction.replace(/_/g, ' ')}` : ''}`, 10, [60, 60, 60]);
      y += 4;
      addDivider();

      // Log entries
      filteredLogs.forEach((log, idx) => {
        checkPage(20);
        addText(`${idx + 1}. ${log.action.replace(/_/g, ' ')}`, 10, [30, 30, 30], true);
        if (log.actionDescription) {
          addText(log.actionDescription, 9, [80, 80, 80]);
        }
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(130, 130, 130);
        pdf.text(
          sanitize(`${log.entityType} · ${log.entityId} · ${log.severity?.toUpperCase()} · ${log.success ? 'Success' : 'Failed'} · ${new Date(log.timestamp).toLocaleString()}`),
          margin, y
        );
        y += 5;
        addDivider();
      });

      // Footer
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setDrawColor(220, 220, 220);
        pdf.line(margin, pageHeight - 14, pageWidth - margin, pageHeight - 14);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(150, 150, 150);
        pdf.text(`My Activity Log · Page ${i} of ${totalPages}`, margin, pageHeight - 8);
        const copyright = 'Copyright FutureiX Inc. | info@futureix.ca';
        const copyrightWidth = pdf.getTextWidth(copyright);
        pdf.text(copyright, (pageWidth - copyrightWidth) / 2, pageHeight - 8);
        pdf.text(new Date().toLocaleDateString(), pageWidth - margin - 20, pageHeight - 8);
      }

      pdf.save(`profix-activity-log-${new Date().toISOString().split('T')[0]}.pdf`);
      showNotification('PDF downloaded!', 'success');
    } catch (error) {
      console.error('PDF generation error:', error);
      showNotification('Failed to generate PDF', 'error');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const loadRoleActions = async () => {
    if (roleActions.length > 0) return;
    const role = isStudent ? 'student' : isInstructor ? 'instructor' : 'admin';
    const response = await getActionsByRole(role);
    if (response.success) {
      setRoleActions(response.data.actions);
    }
  };

  const filteredLogs = activityLogs.filter(log =>
    filterAction === 'all' ? true : log.action === filterAction
  );

  const handleDisable2FA = async () => {
    const result = await disable2FA();
    if (result.success) {
      setIs2FAEnabled(false);
      setBackupCodesRemaining(0);
      showNotification('Two-Factor Authentication has been disabled', 'success');
    } else {
      showNotification('Failed to disable 2FA. Please try again.', 'error');
    }
  };

  // Add this handler function
  const handle2FAToggle = () => {
    if (is2FAEnabled) {
      // If already enabled, show CustomAlert to confirm disable
      setAlertConfig({
        title: 'Disable Two-Factor Authentication',
        icon: '⚠️',
        message: 'Are you sure you want to disable Two-Factor Authentication?\n\nThis will make your account less secure.',
        onConfirm: () => {
          handleDisable2FA();
          setAlertVisible(false);
        }
      });
      setAlertVisible(true);
    } else {
      // Show modal to enable
      setShow2FAModal(true);
    }
  };


  if (isLoading) {
    return (
      <div className="main-container">
        <div className="main-content">
          <div className="profile-loading">
            <div className="loader"></div>
            <p>Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container">
      <div className="main-content">
        
        {/* Header */}
        <div className="header-row">
          <ProfileHeader data={authData} activePage="profile" showBackArrow={true}/>
          <div className="headerMiddle">
            <img src="/images/prof_ix.png" alt="Logo" className="header-logo" />
          </div>
          <SharedHeader notificationCount={0} icons={[]} />
        </div>
        
        {/* Page Title */}
        <div className="page-title">
          <h2>👤 My Profile</h2>
          <p className="page-subtitle">
            View and edit your account information
          </p>
        </div>

        {/* Main Content */}
        <div className="scrollable-list">
          <div className="profile-modern-container">
            <div className="profile-card">
              
              {/* ✅ Compact Header with Avatar */}
              <div className="profile-header-compact">
                <div className="profile-header-background-compact"></div>
                <div className="profile-header-info">
                  {authData?.photo ? (
                    <img 
                      src={authData.photo} 
                      alt={authData?.name} 
                      className="profile-avatar-compact"
                    />
                  ) : (
                    <div className="profile-avatar-placeholder-compact">
                      {authData?.name?.charAt(0) || 'U'}
                    </div>
                  )}
                  <div className="profile-header-text">
                    <h2 className="profile-name-compact">{profileData?.name || authData?.name}</h2>
                    <p className="profile-email-compact">{profileData?.email || authData?.email}</p>
                    <span className="profile-badge-compact">
                      {isStudent && '🎓 Student'}
                      {isInstructor && '👨‍🏫 Instructor'}
                      {isAdmin && '⚙️ Administrator'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Profile Form */}
              <div className="profile-form">
                
                {/* Basic Information Section */}
                <div className="profile-section">
                  <h3 className="profile-section-title">
                    📋 Basic Information
                  </h3>

                  {/* ✅ Two-column layout for Phone and DOB */}
                  <div className="pp-basic-info-grid">
                    {/* Phone number */}
                    <div className="profile-field-modern">
                      <label className="profile-label">
                        📱 Phone <span className="profile-required">*</span>
                      </label>
                      {isEditing ? (
                        <>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone || ''}
                            onChange={handleChange}
                            placeholder="(123) 456-7890"
                            maxLength={14}
                            className={`profile-input ${errors.phone ? 'profile-value-error' : ''}`}
                          />
                          {errors.phone && (
                            <span className="profile-error-message">
                              ⚠️ {errors.phone}
                            </span>
                          )}
                        </>
                      ) : (
                        <p className="profile-value">{profileData?.phone || 'Not provided'}</p>
                      )}
                    </div>

                    {/* DOB */}
                    <div className="profile-field-modern">
                      <label className="profile-label">
                        📅 Date of Birth
                      </label>
                      {isEditing ? (
                        <>
                          <input
                            type="date"
                            name="dateOfBirth"
                            value={formData.dateOfBirth}
                            onChange={handleChange}
                            className={`profile-input ${errors.dateOfBirth ? 'profile-value-error' : ''}`}
                          />
                          {errors.dateOfBirth && (
                            <span className="profile-error-message">
                              ⚠️ {errors.dateOfBirth}
                            </span>
                          )}
                        </>
                      ) : (
                        <p className="profile-value">{profileData?.dateOfBirth || 'Not provided'}</p>
                      )}
                    </div>

                    {/* Gender - Full Width */}
                    <div className="profile-field-modern">
                      <label className="profile-label">
                        👤 Gender
                      </label>
                      {isEditing ? (
                        <select
                          name="gender"
                          value={formData.gender || ''}
                          onChange={handleChange}
                          className="profile-input"
                        >
                          <option value="">Select gender...</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                      ) : (
                        <p className="profile-value">{profileData?.gender || 'Not specified'}</p>
                      )}
                    </div>
                  </div>
                </div>  

                {/* Instructor Professional Information */}
                {isInstructor && (
                  <div className="profile-section">
                    <h3 className="profile-section-title">
                      🎓 Professional Information
                    </h3>

                    {/* ✅ Two-column layout for Title and Department */}
                    <div className="pp-basic-info-grid">
                      {/* Title */}
                      <div className="profile-field-modern">
                        <label className="profile-label">
                          👔 Title <span className="profile-required">*</span>
                        </label>
                        {isEditing ? (
                          <>
                            <input
                              type="text"
                              name="title"
                              value={formData.title || ''}
                              onChange={handleChange}
                              placeholder="Professor, Dr., Lecturer"
                              className={`profile-input ${errors.title ? 'profile-value-error' : ''}`}
                            />
                            {errors.title && (
                              <span className="profile-error-message">
                                ⚠️ {errors.title}
                              </span>
                            )}
                          </>
                        ) : (
                          <p className="profile-value">{profileData?.title || 'Not provided'}</p>
                        )}
                      </div>

                      {/* Qualifications - Full Width */}
                      <div className="profile-field-modern">
                        <label className="profile-label">
                          🎯 Qualifications
                          {isEditing && <span className="profile-helper-text">(comma-separated)</span>}
                        </label>
                        {isEditing ? (
                          <input
                            type="text"
                            name="qualifications"
                            value={Array.isArray(formData.qualifications) ? formData.qualifications.join(', ') : formData.qualifications || ''}
                            onChange={handleChange}
                            placeholder="PhD in Computer Science, 10 years experience"
                            className="profile-input"
                          />
                        ) : (
                          <div className="profile-tags">
                            {profileData?.qualifications?.map((qual, idx) => (
                              <span key={idx} className="profile-tag">{qual}</span>
                            ))}
                            {(!profileData?.qualifications || profileData.qualifications.length === 0) && (
                              <p className="profile-value">No qualifications listed</p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Expertise - Full Width */}
                      <div className="profile-field-modern">
                        <label className="profile-label">
                          💡 Expertise <span className="profile-required">*</span>
                          {isEditing && <span className="profile-helper-text">(comma-separated)</span>}
                        </label>
                        {isEditing ? (
                          <>
                            <input
                              type="text"
                              name="expertise"
                              value={Array.isArray(formData.expertise) ? formData.expertise.join(', ') : formData.expertise || ''}
                              onChange={handleChange}
                              placeholder="IoT, Embedded Systems, Cloud Computing"
                              className={`profile-input ${errors.expertise ? 'profile-value-error' : ''}`}
                            />
                            {errors.expertise && (
                              <span className="profile-error-message">
                                ⚠️ {errors.expertise}
                              </span>
                            )}
                          </>
                        ) : (
                          <div className="profile-tags">
                            {profileData?.expertise?.map((exp, idx) => (
                              <span key={idx} className="profile-tag">{exp}</span>
                            ))}
                            {(!profileData?.expertise || profileData.expertise.length === 0) && (
                              <p className="profile-value">No expertise listed</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Bio - Full Width */}
                    <div className="profile-field-modern">
                      <label className="profile-label">
                        📝 Bio <span className="profile-required">*</span>
                        {isEditing && (
                          <span className="profile-char-counter">
                            ({formData?.bio?.length || 0}/1000 characters)
                          </span>
                        )}
                      </label>
                      {isEditing ? (
                        <>
                          <textarea
                            name="bio"
                            value={formData.bio || ''}
                            onChange={handleChange}
                            placeholder="Tell us about yourself..."
                            rows={4}
                            maxLength={1000}
                            className={`profile-textarea ${errors.bio ? 'profile-value-error' : ''}`}
                          />
                          {errors.bio && (
                            <span className="profile-error-message">
                              ⚠️ {errors.bio}
                            </span>
                          )}
                        </>
                      ) : (
                        <p className="profile-value profile-bio">{profileData?.bio || 'No bio provided'}</p>
                      )}
                    </div>


                    {/* ✅ Two-column layout for Office Location and Hours */}
                    <div className="pp-basic-info-grid">
                      {/* Department */}
                      <div className="profile-field-modern">
                        <label className="profile-label">
                          🏢 Department <span className="profile-required">*</span>
                        </label>
                        {isEditing ? (
                          <>
                            <input
                              type="text"
                              name="department"
                              value={formData.department || ''}
                              onChange={handleChange}
                              placeholder="Computer Science, Engineering"
                              className={`profile-input ${errors.department ? 'profile-value-error' : ''}`}
                            />
                            {errors.department && (
                              <span className="profile-error-message">
                                ⚠️ {errors.department}
                              </span>
                            )}
                          </>
                        ) : (
                          <p className="profile-value">{profileData?.department || 'Not provided'}</p>
                        )}
                      </div>

                      {/* Office Location */}
                      <div className="profile-field-modern">
                        <label className="profile-label">
                          📍 Office Location
                        </label>
                        {isEditing ? (
                          <input
                            type="text"
                            name="officeLocation"
                            value={formData.officeLocation || ''}
                            onChange={handleChange}
                            placeholder="Building A, Room 305"
                            className="profile-input"
                          />
                        ) : (
                          <p className="profile-value">{profileData?.officeLocation || 'Not provided'}</p>
                        )}
                      </div>

                      {/* Office Hours */}
                      <div className="profile-field-modern">
                        <label className="profile-label">
                          🕐 Office Hours
                        </label>
                        {isEditing ? (
                          <input
                            type="text"
                            name="officeHours"
                            value={formData.officeHours || ''}
                            onChange={handleChange}
                            placeholder="Mon-Wed 2-4 PM"
                            className="profile-input"
                          />
                        ) : (
                          <p className="profile-value">{profileData?.officeHours || 'Not provided'}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Alert */}
                {errors.submit && (
                  <div className="profile-error-alert">
                    <span className="profile-error-icon">❌</span>
                    <span>{errors.submit}</span>
                  </div>
                )}

                {/* Two-Factor Authentication Section */}
                {!isAdmin && (
                  <div className="profile-section">
                    <h3 className="profile-section-title">
                      🔐 Security
                    </h3>

                    <div className="profile-field-modern security-field">
                      <div className="twofactor-status-inline">
                        <div className="twofactor-info">
                          <label className="profile-label">Two-Factor Authentication</label>
                          {is2FAEnabled ? (
                            <>
                              <span className="status-badge enabled-inline">✅ Enabled</span>
                              <p className="twofactor-description">
                                Your account is protected with 2FA.
                                {backupCodesRemaining > 0 && (
                                  <span className="backup-count">
                                    {' '}• {backupCodesRemaining} backup code{backupCodesRemaining !== 1 ? 's' : ''} remaining
                                  </span>
                                )}
                              </p>
                            </>
                          ) : (
                            <>
                              <span className="status-badge disabled-inline">⚠️ Not Enabled</span>
                              <p className="twofactor-description">
                                Add an extra layer of security to your account.
                              </p>
                            </>
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={handle2FAToggle}
                          className="twofactor-manage-btn"
                        >
                          {is2FAEnabled ? 'Disable' : 'Enable'} 2FA
                        </button>
                        <CustomAlert
                          visible={alertVisible}
                          title={alertConfig.title}
                          icon={alertConfig.icon}
                          message={alertConfig.message}
                          onConfirm={alertConfig.onConfirm}
                          onCancel={() => setAlertVisible(false)}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* AI Token Usage Section */}
                {tokenSummary && (
                  <div className="profile-section">
                    <h3 className="profile-section-title">
                      🔢 AI Token Usage
                    </h3>

                    <div className="profile-field-modern">
                      <div className="twofactor-status-inline">
                        <div className="twofactor-info">
                          <label className="profile-label">
                            {tokenSummary.totalTokens.toLocaleString()} / {tokenSummary.maxAllowedTokens.toLocaleString()} tokens used
                          </label>
                          <p className="twofactor-description">
                            {tokenSummary.remainingTokens.toLocaleString()} tokens remaining
                            {tokenSummary.lastUsedAt && (
                              <span className="backup-count">
                                {' '}• last used {new Date(tokenSummary.lastUsedAt).toLocaleString()}
                              </span>
                            )}
                          </p>
                          <div style={{
                            marginTop: '8px',
                            height: '8px',
                            width: '100%',
                            maxWidth: '320px',
                            background: '#e5e7eb',
                            borderRadius: '4px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              height: '100%',
                              width: `${tokenSummary.percentUsed}%`,
                              background: tokenSummary.percentUsed >= 90 ? '#dc2626' : tokenSummary.percentUsed >= 70 ? '#f59e0b' : '#16a34a',
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Activity & Privacy Section */}
                <div className="profile-section">
                  <h3 className="profile-section-title">
                    🛡️ Activity & Privacy
                  </h3>

                  <div className="pp-privacy-cards-grid">
                    {/* View Activity Log */}
                    <button
                      className="pp-privacy-card"
                      onClick={() => {
                        setShowActivityModal(true);
                        handleLoadActivityLogs();
                      }}
                    >
                      <span className="pp-privacy-card-icon">📋</span>
                      <div className="pp-privacy-card-content">
                        <h4>My Activity Log</h4>
                        <p>View all your platform activity</p>
                      </div>
                      {/* <span className="pp-privacy-card-arrow">→</span> */}
                    </button>

                    {/* Export My Data */}
                    <button
                      className="pp-privacy-card"
                      onClick={handleExportActivities}
                      disabled={isExporting}
                    >
                      <span className="pp-privacy-card-icon">📥</span>
                      <div className="pp-privacy-card-content">
                        <h4>{isExporting ? 'Exporting...' : 'Export My Data'}</h4>
                        <p>Download all your activity as JSON</p>
                      </div>
                      {isExporting && (
                        <div className="loader profile-loader"></div>
                      )}
                      {/* {isExporting ? (
                        <div className="loader profile-loader"></div>
                      ) : (
                        <span className="pp-privacy-card-arrow">→</span>
                      )} */}
                    </button>

                    {/* Delete Account - students only */}
                    {isStudent && (
                      <button
                        className="pp-privacy-card pp-danger"
                        onClick={() => setShowDeleteModal(true)}
                      >
                        <span className="pp-privacy-card-icon">🗑️</span>
                        <div className="pp-privacy-card-content">
                          <h4>Delete My Account</h4>
                          <p>Permanently remove your account</p>
                        </div>
                        {/* <span className="pp-privacy-card-arrow">→</span> */}
                      </button>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="action-buttons">
                  {isEditing ? (
                    <>
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={isSaving}
                        className="general-btn primary-btn"
                      >
                        {isSaving ? (
                          <>
                            <div className="loader profile-loader"></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <span className="btn-icon">✅</span>
                            Save Changes
                          </>
                        )}
                      </button>
                      
                      <button
                        type="button"
                        onClick={handleCancel}
                        disabled={isSaving}
                        className="general-btn secondary-btn"
                      >
                        <span className="btn-icon">❌</span>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      {!isAdmin && (
                        <button
                          type="button"
                          onClick={() => {
                            setFormData(profileData);  
                            setIsEditing(true);
                          }}
                          className="general-btn primary-btn"
                        >
                          <span className="btn-icon">✏️</span>
                          Edit Profile
                        </button>
                      )}
                      
                    </>
                  )}
                
                </div>

              </div>
              
            </div>
          </div>
        </div>

      </div>

       
        {/* Delete Account Modal */}
        {showDeleteModal && (
          <div className="pp-modal-overlay">
            <div className="pp-modal pp-delete-modal">
              <div className="pp-modal-header">
                <div className="pp-modal-header-info">
                  <h3>🗑️ Delete My Account</h3>
                  <p>This action cannot be undone</p>
                </div>
                <button
                  className="pp-modal-close-btn"
                  onClick={() => setShowDeleteModal(false)}
                >
                  ✖
                </button>
              </div>

              <div className="pp-modal-body">
                <div className="pp-delete-warning">
                  <span className="pp-delete-warning-icon">⚠️</span>
                  <div>
                    <h4>What will be deleted:</h4>
                    <ul>
                      <li>Your profile and personal information</li>
                      <li>All course enrollments and progress</li>
                      <li>All submissions and grades</li>
                      <li>Payment history</li>
                    </ul>
                  </div>
                </div>

                <div className="pp-delete-info">
                  <span className="pp-delete-info-icon">✅</span>
                  <div>
                    <h4>What you retain:</h4>
                    <ul>
                      <li>Access to your activity logs for 1 year</li>
                      <li>Ability to export your data</li>
                    </ul>
                  </div>
                </div>

                <div className="pp-delete-actions">
                  <button
                    className="pp-delete-cancel-btn"
                    onClick={() => setShowDeleteModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="pp-delete-confirm-btn"
                    onClick={() => {
                      setShowDeleteModal(false);
                      navigate('/delete-account');
                    }}
                  >
                    Continue to Delete →
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Activity Log Modal */}
        {showActivityModal && (
          <div className="pp-modal-overlay">
            <div className="pp-modal">

              {/* Header */}
              <div className="pp-modal-header">
                <div className="pp-modal-header-info">
                  <h3>📋 My Activity Log</h3>
                  <p>{filteredLogs.length} records</p>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    className="pp-modal-download-btn"
                    onClick={handleDownloadActivityPDF}
                    disabled={isGeneratingPDF || filteredLogs.length === 0}
                    title="Download as PDF"
                  >
                    {isGeneratingPDF ? '⏳' : '📄 PDF'}
                  </button>
                  <button
                    className="pp-modal-close-btn"
                    onClick={() => {
                      setShowActivityModal(false);
                      setFilterAction('all');
                      setExpandedLogId(null);
                    }}
                  >
                    ✖
                  </button>
                </div>
              </div>

              {/* Filter bar */}
              <div className="pp-modal-filter-bar">
                <label>Filter by action:</label>
                <select
                  value={filterAction}
                  onChange={(e) => setFilterAction(e.target.value)}
                  className="pp-modal-filter-select"
                >
                  <option value="all">All Actions</option>
                  {roleActions.map(action => (
                    <option key={action} value={action}>
                      {action.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
                <span className="pp-modal-record-count">{filteredLogs.length} records</span>
              </div>

              {/* Body */}
              <div className="pp-modal-body">
                {activityLoading ? (
                  <div className="pp-modal-loading">
                    <div className="loader"></div>
                    <p>Loading activities...</p>
                  </div>
                ) : filteredLogs.length === 0 ? (
                  <div className="pp-modal-empty">
                    <p>📭</p>
                    <p>No activity records found</p>
                  </div>
                ) : (
                  filteredLogs.map((log, index) => {
                    const isExpanded = expandedLogId === index;
                    return (
                      <div
                        key={index}
                        className="pp-log-item"
                        onClick={() => setExpandedLogId(isExpanded ? null : index)}
                      >
                        <div className="pp-log-item-main">
                          <p className="pp-log-item-action">
                            {log.action.replace(/_/g, ' ')}
                          </p>
                          {log.actionDescription && (
                            <p className="pp-log-item-desc">{log.actionDescription}</p>
                          )}
                          <span className="pp-log-item-time">
                            {getTimeAgo(new Date(log.timestamp))}
                          </span>
                          {isExpanded && (
                            <div className="pp-log-item-details">
                              <p><strong>Entity:</strong> {log.entityType} — {log.entityId}</p>
                              <p>
                                <strong>Severity:</strong>{' '}
                                <span className={`pp-log-severity ${log.severity}`}>
                                  {log.severity}
                                </span>
                              </p>
                              <p><strong>Status:</strong> {log.success ? '✅ Success' : '❌ Failed'}</p>
                              <p><strong>Time:</strong> {new Date(log.timestamp).toLocaleString()}</p>
                            </div>
                          )}
                        </div>
                        <span className="pp-log-item-expand">
                          {isExpanded ? '▼' : '▶'}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>

            </div>
          </div>
        )}
     
        {/* ✅ Add 2FA Modal */}
        {show2FAModal && (
          <TwoFactorModal 
            onClose={() => {
              setShow2FAModal(false);
              // Reload 2FA status
              const load2FAStatus = async () => {
                const status = await check2FAStatus();
                if (status.success) {
                  setIs2FAEnabled(status.enabled);
                  setBackupCodesRemaining(status.backupCodesRemaining || 0);
                }
              };
              load2FAStatus();
            }}
            onSuccess={() => {
              // alert('✅ Two-Factor Authentication enabled successfully!');
              showNotification('Two-Factor Authentication enabled successfully!', 'success')
            }}
          />
        )}
    </div>
  );

};

export default Profile;